import { NexusRoutes } from '../routes';
import { ShowingAvailability, Rental } from '../models';
import {
  FetchRentalsRequest,
  FetchRentalsResponse,
  FetchRentalDetailsRequest,
  FetchRentalDetailsResponse,
  FetchShowingAvailabilityRequest,
  FetchShowingAvailabilityResponse,
  FetchSimilarRentalsRequest,
  FetchSimilarRentalsResponse,
  ScheduleShowingRequest,
  ScheduleShowingResponse,
  CancelShowingByIdAndEmailRequest,
  CancelShowingRequest,
  CancelShowingResponse,
  GetAgentRequest,
  GetAgentResponse,
  UpdateOrCreateAgentRequest,
  UpdateOrCreateAgentResponse,
  CreateLeadRequest,
  FetchLeadByUuidRequest,
  FetchLeadByUuidResponse,
} from './APIClientInterface';
import { BaseAPIClient, APIResponse } from './BaseAPIClient';

export class APIClient extends BaseAPIClient {
  static async fetchRentals(request: FetchRentalsRequest): Promise<FetchRentalsResponse> {
    const { client, config } = this.createClient();
    const params = new URLSearchParams();

    // Add query params to the request
    if (request.minPrice) params.append('min_price', request.minPrice);
    if (request.maxPrice) params.append('max_price', request.maxPrice);
    if (request.minBedrooms) params.append('min_bedrooms', request.minBedrooms);
    if (request.maxBedrooms) params.append('max_bedrooms', request.maxBedrooms);
    if (request.minBathrooms) params.append('min_bathrooms', request.minBathrooms);
    if (request.moveInAfter) params.append('move_in_after', request.moveInAfter);
    if (request.moveInBefore) params.append('move_in_before', request.moveInBefore);
    if (request.amenities) params.append('amenities', request.amenities);

    const url = `${NexusRoutes.RentalListings}?${params.toString()}`;

    return client
      .get(url, config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchRentalsResponse>(request, httpResponse);
        if (response.success) {
          response.rentals = httpResponse.data.results;
          response.nextPageUrl = httpResponse.data.next;
        }
        return response;
      })
      .catch(err => {
        return this.prepareResponse<FetchRentalsResponse>(request, err);
      });
  }

  static async fetchRentalById(request: FetchRentalDetailsRequest): Promise<FetchRentalDetailsResponse> {
    if (!request.id) {
      throw new Error('Rental ID is missing.');
    }

    const { client, config } = this.createClient();
    return client
      .get(NexusRoutes.RentalDetails.replace(':rentalId', request.id.toString()), config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchRentalDetailsResponse>(request, httpResponse);
        if (response.success) response.rental = httpResponse.data;
        return response;
      })
      .catch(err => {
        return this.prepareResponse<FetchRentalDetailsResponse>(request, err);
      });
  }

  static async fetchAvailableShowingTimes(
    request: FetchShowingAvailabilityRequest
  ): Promise<FetchShowingAvailabilityResponse> {
    if (!request.rentalId) {
      throw new Error('Rental ID is missing.');
    }

    const { client, config } = this.createClient();

    const queryParams = new URLSearchParams();
    if (request.isAgent) {
      queryParams.append('agent', 'true');
    }

    return client
      .get(
        `${NexusRoutes.RentalAvailableTimes.replace(
          ':rentalId',
          request.rentalId.toString()
        )}?${queryParams.toString()}`,
        config
      )
      .then(httpResponse => {
        const response = this.prepareResponse<FetchShowingAvailabilityResponse>(request, httpResponse);

        if (response.success && httpResponse.data.available_times) {
          const transformedTimes: ShowingAvailability[] = Object.entries(httpResponse.data.available_times).map(
            ([date, times]) => ({
              date,
              times: times as string[],
            })
          );
          response.availableTimes = transformedTimes;
        } else {
          response.availableTimes = [];
        }

        return response;
      })
      .catch(err => {
        return this.prepareResponse<FetchShowingAvailabilityResponse>(request, err);
      });
  }

  static async fetchSimilarRentals(request: FetchSimilarRentalsRequest): Promise<FetchSimilarRentalsResponse> {
    if (!request.rentalId) {
      throw new Error('Rental ID is missing.');
    }

    const { client, config } = this.createClient();

    return client
      .get(NexusRoutes.RentalSimilar.replace(':rentalId', request.rentalId.toString()), config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchSimilarRentalsResponse>(request, httpResponse);
        if (response.success) {
          response.rentals = httpResponse.data as Rental[];
        }
        return response;
      })
      .catch(err => {
        return this.prepareResponse<FetchSimilarRentalsResponse>(request, err);
      });
  }

  static async scheduleShowing(request: ScheduleShowingRequest): Promise<ScheduleShowingResponse> {
    console.log(request);
    if (!request.rental_listing_id || !request.datetime || !request.lead?.first_name || !request.lead?.last_name) {
      throw new Error('Required lead fields are missing.');
    }

    if (request.agent) {
      const requiredAgentFields: Array<keyof typeof request.agent> = [
        'first_name',
        'last_name',
        'company_name',
        'company_email',
        'agent_license_id',
      ];
      const missingAgentFields = requiredAgentFields.filter(field => !request.agent?.[field]);
      if (missingAgentFields.length > 0) {
        throw new Error(`Missing required agent fields: ${missingAgentFields.join(', ')}`);
      }
    }

    const { client, config } = this.createClient();
    const url = NexusRoutes.ScheduleShowing;

    return client
      .post(url, request, config)
      .then(httpResponse => {
        const response = this.prepareResponse<ScheduleShowingResponse>(request, httpResponse);
        if (response.success) {
          response.id = httpResponse.data.id;
          response.lead = httpResponse.data.lead ?? null;
        }
        return response;
      })
      .catch(err => {
        const appError = {
          message: err.response?.data?.message || err.response?.data?.detail || 'An unknown error occurred.',
          code: err.response?.status || 500,
          details: err.response?.data || null,
        };
        return this.prepareResponse<ScheduleShowingResponse>(request, {
          ...err,
          data: appError,
        });
      });
  }

  static async cancelShowingByIdAndEmail(request: CancelShowingByIdAndEmailRequest): Promise<CancelShowingResponse> {
    if (!request.showingId || !request.email) {
      throw new Error('Showing ID and email are required.');
    }

    const { client, config } = this.createClient();
    const url = NexusRoutes.ShowingCancelByIdAndEmail.replace(':showingId', request.showingId.toString());

    return client
      .post(url, { email: request.email }, config)
      .then(httpResponse => {
        return this.prepareResponse<CancelShowingResponse>(request, httpResponse);
      })
      .catch(err => {
        return this.prepareResponse<CancelShowingResponse>(request, err);
      });
  }

  static async cancelShowing(request: CancelShowingRequest): Promise<CancelShowingResponse> {
    if (!request.showingUuid) {
      throw new Error('Showing UUID is required.');
    }

    const { client, config } = this.createClient();
    const url = NexusRoutes.ShowingCancel.replace(':showingUuid', request.showingUuid);

    return client
      .post(url, {}, config)
      .then(httpResponse => {
        return this.prepareResponse<CancelShowingResponse>(request, httpResponse);
      })
      .catch(err => {
        return this.prepareResponse<CancelShowingResponse>(request, err);
      });
  }

  static async updateOrCreateAgent(request: UpdateOrCreateAgentRequest): Promise<UpdateOrCreateAgentResponse> {
    const { client, config } = this.createClient();
    const url = NexusRoutes.RentalAgentProfile;

    const formData = new FormData();
    formData.append('company_email', request.company_email);
    formData.append('first_name', request.first_name);
    formData.append('last_name', request.last_name);
    formData.append('company_name', request.company_name);
    formData.append('phone', request.phone);
    formData.append('agent_license_id', request.agent_license_id);

    if (request.license_photo) {
      formData.append('license_photo', request.license_photo);
    }

    return client
      .post(url, formData, {
        ...config,
        headers: {
          ...config.headers,
          'Content-Type': 'multipart/form-data',
        },
      })
      .then(httpResponse => {
        const response = this.prepareResponse<UpdateOrCreateAgentResponse>(request, httpResponse);
        if (response.success) {
          response.verified = httpResponse.data.verified;
          response.id = httpResponse.data.id;
        }
        return response;
      })
      .catch(err => {
        return this.prepareResponse<UpdateOrCreateAgentResponse>(request, err);
      });
  }

  static async getAgent(request: GetAgentRequest): Promise<GetAgentResponse> {
    const { client, config } = this.createClient();
    const url = `${NexusRoutes.RentalAgentProfile}?company_email=${encodeURIComponent(
      request.company_email
    )}&agent_license_id=${encodeURIComponent(request.agent_license_id)}`;

    return client
      .get(url, config)
      .then(httpResponse => {
        const response = this.prepareResponse<GetAgentResponse>(request, httpResponse);
        if (response.success) {
          response.verified = httpResponse.data.verified;
          response.id = httpResponse.data.id;
        }
        return response;
      })
      .catch(err => {
        return this.prepareResponse<GetAgentResponse>(request, err);
      });
  }

  static async createLead(request: CreateLeadRequest): Promise<APIResponse> {
    const { client, config } = this.createClient();
    const url = NexusRoutes.LeadsCreate;

    return client
      .post(url, request, config)
      .then(httpResponse => {
        return this.prepareResponse<APIResponse>(request, httpResponse);
      })
      .catch(err => {
        return this.prepareResponse<APIResponse>(request, err);
      });
  }

  static async fetchLeadByUuid(request: FetchLeadByUuidRequest): Promise<FetchLeadByUuidResponse> {
    const { client, config } = this.createClient();
    const url = NexusRoutes.LeadDetails.replace(':uuid', request.uuid);

    return client
      .get(url, config)
      .then(httpResponse => {
        const response = this.prepareResponse<FetchLeadByUuidResponse>(request, httpResponse);
        if (response.success) {
          response.lead = httpResponse.data;
        }
        return response;
      })
      .catch(err => {
        return this.prepareResponse<FetchLeadByUuidResponse>(request, err);
      });
  }
}
